import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import type { WorkshopProject, ElectricalWire, WireColor, ElectricalDevice } from '../../model/types';
import { PlasticEnclosureSvg } from '@/ui/scene/components/PlasticEnclosureSvg';
import { McbSvg } from './components/McbSvg';
import { ContactorSvg } from './components/ContactorSvg';
import { ThermalRelaySvg } from './components/ThermalRelaySvg';
import { AcMotor3PhaseSvg } from '@/ui/scene/components/AcMotor3PhaseSvg';
import { EnclosureDoorSvg } from './components/EnclosureDoorSvg';
import { WireDuctSvg } from './components/WireDuctSvg';
import { WireRenderer } from './WireRenderer';
import { ElectricalSolver } from '../../solver/ElectricalSolver';
import { workshopAudio } from '../../audio/workshopAudio';
import { useWorkshopStore } from '../../store/workshopStore';
import {
  createTopWireDuct,
  createMidWireDuct,
  createLeftWireDuct,
  createRightWireDuct,
  createMcb3P,
  createMcb1P,
  createContactor3P,
  createThermalRelay,
  createStopButton,
  createStartButton,
  createPilotLamp,
  createMotor3Phase,
} from '../../model/catalog';
import type { Vec2 } from '@/core/types';

export interface PanelViewProps {
  project: WorkshopProject;
  onChangeProject?: (updated: WorkshopProject) => void;
  solver?: ElectricalSolver;
  activeWireColor?: WireColor;
  isSimulating?: boolean;
}

/**
 * PanelSlotGuide: Panoda veya rayda henüz yerleştirilmemiş eleman yuvasını
 * kesikli çizgiler ve tıklanabilir rehber ile gösteren endüstriyel SVG yuva bileşeni.
 */
interface PanelSlotGuideProps {
  x: number;
  y: number;
  width: number;
  height: number;
  label: string;
  sublabel?: string;
  orientation?: 'horizontal' | 'vertical';
  isHighlighted?: boolean;
  onClick: () => void;
}

const PanelSlotGuide: React.FC<PanelSlotGuideProps> = ({
  x,
  y,
  width,
  height,
  label,
  sublabel,
  orientation = 'horizontal',
  isHighlighted = false,
  onClick,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const active = isHighlighted || isHovered;

  const strokeColor = active ? '#38bdf8' : '#475569';
  const fillColor = active ? 'rgba(56, 189, 248, 0.14)' : 'rgba(30, 41, 59, 0.35)';
  const textColor = active ? '#38bdf8' : '#94a3b8';

  return (
    <g
      transform={`translate(${x}, ${y})`}
      style={{ cursor: 'pointer', pointerEvents: 'auto' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <title>{`${label} — Tıklayarak panoya yerleştirin`}</title>
      <rect
        x={0}
        y={0}
        width={width}
        height={height}
        rx={5}
        ry={5}
        fill={fillColor}
        stroke={strokeColor}
        strokeWidth={active ? 2 : 1.2}
        strokeDasharray="5,4"
        style={{ transition: 'all 0.15s ease' }}
      />

      {orientation === 'vertical' ? (
        <g transform={`translate(${width / 2}, ${height / 2}) rotate(-90)`}>
          <text
            x={0}
            y={3}
            fill={textColor}
            fontSize={8}
            fontWeight={active ? 'bold' : 'normal'}
            textAnchor="middle"
            fontFamily="sans-serif"
          >
            {label}
          </text>
        </g>
      ) : (
        <g transform={`translate(${width / 2}, ${height / 2})`}>
          <text
            x={0}
            y={sublabel ? -2 : 3}
            fill={textColor}
            fontSize={sublabel ? 9 : 8}
            fontWeight="bold"
            textAnchor="middle"
            fontFamily="sans-serif"
          >
            {label}
          </text>
          {sublabel && (
            <text
              x={0}
              y={11}
              fill={active ? '#7dd3fc' : '#64748b'}
              fontSize={7.5}
              textAnchor="middle"
              fontFamily="sans-serif"
            >
              {sublabel}
            </text>
          )}
        </g>
      )}

      {/* Tıklayarak Yerleştir Rozeti / İkonu */}
      {active && (
        <g
          transform={
            orientation === 'vertical'
              ? `translate(${width / 2}, 14)`
              : `translate(14, ${height / 2})`
          }
        >
          <circle cx="0" cy="0" r="6" fill="#0284c7" stroke="#38bdf8" strokeWidth="1" />
          <text
            x="0"
            y="3"
            fill="#ffffff"
            fontSize="8"
            fontWeight="bold"
            textAnchor="middle"
          >
            +
          </text>
        </g>
      )}
    </g>
  );
};

/**
 * Elektrik Kumanda Atölyesi — Fiziksel Pano Görünümü (PanelView)
 * Gerçekçi plastik dağıtım kasası (400x600 mm) ve menteşeli yana açılan pano kapağı
 * zemininde DIN rayı montajlı endüstriyel sigortalar, kontaktör, termik röle,
 * kablo kanalları, kapak üstü 22mm kumanda butonları ve pilot lambaları birleştirir.
 */
export const PanelView: React.FC<PanelViewProps> = ({
  project,
  onChangeProject,
  solver: externalSolver,
  activeWireColor = '#1f2937',
  isSimulating = true,
}) => {
  const solver = useMemo(() => externalSolver || new ElectricalSolver(), [externalSolver]);

  // Store Eylemleri ve Seçim Durumları
  const storeAddDevice = useWorkshopStore((s) => s.addDevice);
  const storeRemoveDevice = useWorkshopStore((s) => s.removeDevice);
  const selectedPaletteItemId = useWorkshopStore((s) => s.selectedPaletteItemId);
  const setSelectedPaletteItem = useWorkshopStore((s) => s.setSelectedPaletteItem);
  const selectedDeviceId = useWorkshopStore((s) => s.selectedDeviceId);
  const setSelectedDeviceId = useWorkshopStore((s) => s.setSelectedDeviceId);

  // Menteşeli Pano Kapağı Açık/Kapalı Durumu (Kablolama için varsayılan: açık)
  const [isDoorOpen, setIsDoorOpen] = useState<boolean>(true);

  // Canlı Simülasyon Durumu
  const [activeTerminalId, setActiveTerminalId] = useState<string | null>(null);
  const [selectedWireId, setSelectedWireId] = useState<string | null>(null);

  const handleToggleDoor = useCallback(() => {
    setIsDoorOpen((prev) => !prev);
    workshopAudio.playBreakerToggle();
  }, []);

  // Projedeki Elemanların Varlık Durumu
  const hasF1 = project.devices.some((d) => d.id === 'f1');
  const hasF2 = project.devices.some((d) => d.id === 'f2');
  const hasKm1 = project.devices.some((d) => d.id === 'km1');
  const hasFr1 = project.devices.some((d) => d.id === 'fr1');
  const hasSb1 = project.devices.some((d) => d.id === 'sb1');
  const hasSb2 = project.devices.some((d) => d.id === 'sb2');
  const hasHl1 = project.devices.some((d) => d.id === 'hl1');
  const hasHl2 = project.devices.some((d) => d.id === 'hl2');
  const hasM1 = project.devices.some((d) => d.id === 'm1');

  // Kablo Kanalları Varlık Durumu
  const ductTop = project.devices.find((d) => d.id === 'duct_top');
  const ductMid = project.devices.find((d) => d.id === 'duct_mid');
  const ductLeft = project.devices.find((d) => d.id === 'duct_left');
  const ductRight = project.devices.find((d) => d.id === 'duct_right');

  // Buton ve sigorta anlık durumları
  const [devicesState, setDevicesState] = useState(() => {
    const map: Record<string, Record<string, unknown>> = {};
    for (const d of project.devices) {
      map[d.id] = { ...d.props };
    }
    return map;
  });

  // Güncel proje durumunu çöz
  const currentProject = useMemo(() => {
    return {
      ...project,
      devices: project.devices.map((d) => ({
        ...d,
        props: { ...d.props, ...(devicesState[d.id] || {}) },
      })),
    };
  }, [project, devicesState]);

  // Anlık elektriksel snapshot (çözümleme)
  const snapshot = useMemo(() => {
    if (!isSimulating) {
      return {
        simTimeMs: 0,
        nodePotentials: {},
        deviceStates: {},
        wireEnergized: {},
        hasShortCircuit: false,
      };
    }
    return solver.solve(currentProject);
  }, [solver, currentProject, isSimulating]);

  // Tüm klemenslerin Pano Koordinat Düzlemindeki (World Vec2) konum haritası
  // Sadece panoya YERLEŞTİRİLMİŞ olan elemanların terminalleri kayıt edilir!
  const terminalPositions = useMemo(() => {
    const posMap: Record<string, Vec2> = {};

    // 1. Şebeke Besleme Rayı (Üst Klemens Barası: X=100, Y=78)
    const pwr = project.devices.find((d) => d.id === 'pwr');
    if (pwr) {
      posMap['pwr:L1'] = { x: 108, y: 92 };
      posMap['pwr:L2'] = { x: 130, y: 92 };
      posMap['pwr:L3'] = { x: 152, y: 92 };
      posMap['pwr:N'] = { x: 273, y: 89 }; // Som bakır Nötr barası
      posMap['pwr:PE'] = { x: 470, y: 89 }; // Som bakır Toprak barası
    }

    // 2. 3P Sigorta (F1: X=100, Y=195)
    if (hasF1) {
      posMap['f1:1'] = { x: 114, y: 209 };
      posMap['f1:3'] = { x: 142, y: 209 };
      posMap['f1:5'] = { x: 170, y: 209 };
      posMap['f1:2'] = { x: 114, y: 293 };
      posMap['f1:4'] = { x: 142, y: 293 };
      posMap['f1:6'] = { x: 170, y: 293 };
    }

    // 3. 1P Sigorta (F2: X=196, Y=195)
    if (hasF2) {
      posMap['f2:1'] = { x: 210, y: 209 };
      posMap['f2:2'] = { x: 210, y: 293 };
    }

    // 4. Güç Kontaktörü (KM1: X=244, Y=190)
    if (hasKm1) {
      posMap['km1:A1'] = { x: 256, y: 204 };
      posMap['km1:1'] = { x: 274, y: 204 };
      posMap['km1:3'] = { x: 292, y: 204 };
      posMap['km1:5'] = { x: 310, y: 204 };
      posMap['km1:13'] = { x: 328, y: 204 };
      posMap['km1:2'] = { x: 264, y: 296 };
      posMap['km1:4'] = { x: 282, y: 296 };
      posMap['km1:6'] = { x: 300, y: 296 };
      posMap['km1:14'] = { x: 318, y: 296 };
      posMap['km1:A2'] = { x: 332, y: 296 };
    }

    // 5. Termik Röle (FR1: X=244, Y=305)
    if (hasFr1) {
      posMap['fr1:1'] = { x: 276, y: 317 };
      posMap['fr1:3'] = { x: 294, y: 317 };
      posMap['fr1:5'] = { x: 312, y: 317 };
      posMap['fr1:95'] = { x: 258, y: 365 };
      posMap['fr1:96'] = { x: 274, y: 365 };
      posMap['fr1:97'] = { x: 310, y: 365 };
      posMap['fr1:98'] = { x: 326, y: 365 };
      posMap['fr1:2'] = { x: 268, y: 387 };
      posMap['fr1:4'] = { x: 292, y: 387 };
      posMap['fr1:6'] = { x: 316, y: 387 };
    }

    // 6. Menteşeli Pano Kapağı Üzerindeki Butonlar ve Lambalar
    if (isDoorOpen) {
      if (hasHl2) {
        posMap['hl2:X1'] = { x: 790, y: 305 };
        posMap['hl2:X2'] = { x: 910, y: 305 };
      }
      if (hasHl1) {
        posMap['hl1:X1'] = { x: 990, y: 305 };
        posMap['hl1:X2'] = { x: 1110, y: 305 };
      }
      if (hasSb2) {
        posMap['sb2:3'] = { x: 790, y: 505 };
        posMap['sb2:4'] = { x: 910, y: 505 };
      }
      if (hasSb1) {
        posMap['sb1:1'] = { x: 990, y: 505 };
        posMap['sb1:2'] = { x: 1110, y: 505 };
      }
    } else {
      if (hasHl1) {
        posMap['hl1:X1'] = { x: 210, y: 280 };
        posMap['hl1:X2'] = { x: 210, y: 280 };
      }
      if (hasHl2) {
        posMap['hl2:X1'] = { x: 390, y: 280 };
        posMap['hl2:X2'] = { x: 390, y: 280 };
      }
      if (hasSb1) {
        posMap['sb1:1'] = { x: 210, y: 480 };
        posMap['sb1:2'] = { x: 210, y: 480 };
      }
      if (hasSb2) {
        posMap['sb2:3'] = { x: 390, y: 480 };
        posMap['sb2:4'] = { x: 390, y: 480 };
      }
    }

    // 7. 3 Fazlı Motor Terminalleri (M1: X=160, Y=530)
    if (hasM1) {
      posMap['m1:U1'] = { x: 184, y: 550 };
      posMap['m1:V1'] = { x: 204, y: 550 };
      posMap['m1:W1'] = { x: 224, y: 550 };
      posMap['m1:PE'] = { x: 244, y: 550 };
    }

    return posMap;
  }, [project, isDoorOpen, hasF1, hasF2, hasKm1, hasFr1, hasSb1, hasSb2, hasHl1, hasHl2, hasM1]);

  // Eleman Panoya Yerleştirme Yardımcısı
  const handlePlaceDevice = useCallback(
    (slotKey: string) => {
      let newDev: ElectricalDevice | null = null;
      switch (slotKey) {
        case 'duct_top':
          newDev = createTopWireDuct();
          break;
        case 'duct_mid':
          newDev = createMidWireDuct();
          break;
        case 'duct_left':
          newDev = createLeftWireDuct();
          break;
        case 'duct_right':
          newDev = createRightWireDuct();
          break;
        case 'f1':
          newDev = createMcb3P('f1', 'F1', 100, 195);
          break;
        case 'f2':
          newDev = createMcb1P('f2', 'F2', 196, 195);
          break;
        case 'km1':
          newDev = createContactor3P('km1', 'KM1', 244, 190);
          break;
        case 'fr1':
          newDev = createThermalRelay('fr1', 'FR1', 244, 305);
          break;
        case 'sb1':
          newDev = createStopButton('sb1', 'SB1', 210, 480);
          break;
        case 'sb2':
          newDev = createStartButton('sb2', 'SB2', 390, 480);
          break;
        case 'hl1':
          newDev = createPilotLamp('hl1', 'HL1', '#22c55e', 210, 280);
          break;
        case 'hl2':
          newDev = createPilotLamp('hl2', 'HL2', '#ef4444', 390, 280);
          break;
        case 'm1':
          newDev = createMotor3Phase('m1', 'M1', 160, 530);
          break;
      }

      if (newDev) {
        if (onChangeProject) {
          onChangeProject({
            ...project,
            devices: [...project.devices.filter((d) => d.id !== newDev!.id), newDev],
          });
        }
        storeAddDevice(newDev);
        workshopAudio.playBreakerToggle();
        setSelectedPaletteItem(null);
      }
    },
    [project, onChangeProject, storeAddDevice, setSelectedPaletteItem]
  );

  // Elemanı Panodan Silme / Kaldırma Yardımcısı
  const handleRemoveDevice = useCallback(
    (deviceId: string) => {
      if (onChangeProject) {
        onChangeProject({
          ...project,
          devices: project.devices.filter((d) => d.id !== deviceId),
          wires: project.wires.filter(
            (w) => w.from.deviceId !== deviceId && w.to.deviceId !== deviceId
          ),
        });
      }
      storeRemoveDevice(deviceId);
      workshopAudio.playBreakerToggle();
      if (selectedDeviceId === deviceId) {
        setSelectedDeviceId(null);
      }
    },
    [project, onChangeProject, storeRemoveDevice, selectedDeviceId, setSelectedDeviceId]
  );

  // Terminal Tıklama — İki terminal arasında kablo çekme
  const handleTerminalClick = useCallback(
    (targetKey: string) => {
      if (!activeTerminalId) {
        setActiveTerminalId(targetKey);
      } else if (activeTerminalId === targetKey) {
        setActiveTerminalId(null);
      } else {
        const [fromDev, fromTerm] = activeTerminalId.split(':');
        const [toDev, toTerm] = targetKey.split(':');

        if (fromDev && fromTerm && toDev && toTerm) {
          const newWire: ElectricalWire = {
            id: `w_${Date.now()}`,
            from: { deviceId: fromDev, terminalId: fromTerm },
            to: { deviceId: toDev, terminalId: toTerm },
            color: activeWireColor,
            sectionMm2: activeWireColor === '#78350f' || activeWireColor === '#84cc16' ? 2.5 : 1.5,
          };

          onChangeProject?.({
            ...project,
            wires: [...project.wires, newWire],
          });
        }
        setActiveTerminalId(null);
      }
    },
    [activeTerminalId, activeWireColor, project, onChangeProject]
  );

  // Kablo Silme
  const handleDeleteSelectedWire = useCallback(() => {
    if (!selectedWireId) return;
    onChangeProject?.({
      ...project,
      wires: project.wires.filter((w) => w.id !== selectedWireId),
    });
    setSelectedWireId(null);
  }, [selectedWireId, project, onChangeProject]);

  // Cihaz Durum Güncelleme Yardımcısı
  const updateDeviceProp = (deviceId: string, propKey: string, value: unknown) => {
    setDevicesState((prev) => ({
      ...prev,
      [deviceId]: {
        ...(prev[deviceId] || {}),
        [propKey]: value,
      },
    }));
  };

  const km1State = snapshot.deviceStates['km1'];
  const fr1State = snapshot.deviceStates['fr1'];
  const m1State = snapshot.deviceStates['m1'];
  const hl1State = snapshot.deviceStates['hl1'];
  const hl2State = snapshot.deviceStates['hl2'];

  // Seçili cihaz nesnesi
  const selectedDevice = useMemo(
    () => project.devices.find((d) => d.id === selectedDeviceId),
    [project.devices, selectedDeviceId]
  );

  // Kontaktör Çekme / Bırakma Ses Geçişleri
  const prevKm1Energized = useRef<boolean>(false);
  useEffect(() => {
    const isEnergized = km1State?.energized ?? false;
    if (isEnergized && !prevKm1Energized.current) {
      workshopAudio.playContactorEnergize();
    } else if (!isEnergized && prevKm1Energized.current) {
      workshopAudio.playContactorRelease();
    }
    prevKm1Energized.current = isEnergized;
  }, [km1State?.energized]);

  const prevHasShortCircuit = useRef<boolean>(false);
  useEffect(() => {
    if (snapshot.hasShortCircuit && !prevHasShortCircuit.current) {
      workshopAudio.playShortCircuitAlarm();
    }
    prevHasShortCircuit.current = snapshot.hasShortCircuit;
  }, [snapshot.hasShortCircuit]);

  return (
    <div
      className="panel-view-container"
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        background: '#090d16',
        padding: '16px',
        overflow: 'auto',
      }}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Delete' || e.key === 'Backspace') {
          if (selectedWireId) {
            handleDeleteSelectedWire();
          } else if (selectedDeviceId && selectedDeviceId !== 'pwr') {
            handleRemoveDevice(selectedDeviceId);
          }
        } else if (e.key === 'Escape') {
          setActiveTerminalId(null);
          setSelectedWireId(null);
          setSelectedDeviceId(null);
          setSelectedPaletteItem(null);
        }
      }}
    >
      {/* Üst Bilgi ve Hızlı Eylem Çubuğu */}
      <div
        style={{
          width: '100%',
          maxWidth: isDoorOpen ? 1280 : 640,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#131a26',
          border: '1px solid #232d3f',
          padding: '8px 16px',
          borderRadius: 8,
          marginBottom: 12,
          fontSize: 13,
          transition: 'max-width 0.2s ease',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, color: '#38bdf8' }}>FİZİKSEL PANO GÖRÜNÜMÜ</span>

          {activeTerminalId && (
            <span style={{ color: '#fbbf24', fontSize: 12 }}>
              Kablo Çekiliyor: <strong>{activeTerminalId}</strong> (Hedef terminale tıklayın)
            </span>
          )}

          {selectedPaletteItemId && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: '#38bdf8', fontSize: 12, fontWeight: 600 }}>
                👉 Yuva Seçin: Kesikli yuvaya tıklayarak yerleştirin
              </span>
              <button
                type="button"
                onClick={() => setSelectedPaletteItem(null)}
                style={{
                  background: '#334155',
                  color: '#e2e8f0',
                  border: 'none',
                  padding: '2px 8px',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: 11,
                }}
              >
                İptal
              </button>
            </div>
          )}

          {selectedDevice && selectedDevice.id !== 'pwr' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: '#f59e0b', fontSize: 12, fontWeight: 600 }}>
                Seçili: {selectedDevice.name}
              </span>
              <button
                type="button"
                onClick={() => handleRemoveDevice(selectedDevice.id)}
                style={{
                  background: '#dc2626',
                  color: '#fff',
                  border: 'none',
                  padding: '2px 8px',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                Panodan Kaldır
              </button>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Menteşeli Pano Kapağını Aç / Kapat Butonu */}
          <button
            type="button"
            onClick={handleToggleDoor}
            style={{
              background: isDoorOpen ? '#0284c7' : '#10b981',
              color: '#ffffff',
              border: 'none',
              padding: '5px 12px',
              borderRadius: 5,
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
            }}
          >
            {isDoorOpen ? '🚪 Kapağı Kapat (Ön Yüz)' : '📂 Kapağı Aç (Kablolama)'}
          </button>

          {selectedWireId && (
            <button
              type="button"
              onClick={handleDeleteSelectedWire}
              style={{
                background: '#dc2626',
                color: '#fff',
                border: 'none',
                padding: '4px 10px',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              Seçili Kabloyu Sil
            </button>
          )}

          {snapshot.hasShortCircuit && (
            <span style={{ color: '#ef4444', fontWeight: 'bold' }}>
              ⚠️ {snapshot.shortCircuitMessage || 'Kısa Devre!'}
            </span>
          )}
        </div>
      </div>

      {/* Pano Çizim Tuvali (SVG Container: Kapak açıkken 1040px, kapalıyken 640px) */}
      <div
        style={{
          position: 'relative',
          width: isDoorOpen ? 1280 : 640,
          height: 840,
          background: 'transparent',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
          transition: 'width 0.2s ease',
        }}
        onClick={() => {
          setSelectedWireId(null);
          setActiveTerminalId(null);
          setSelectedDeviceId(null);
        }}
      >
        {/* 1. Plastik Pano Gövdesi (Arka Plan: 400x600 mm Gerçek Ölçülü, Vidasız, Ayaksız Raylar, Bakır Baralar) */}
        <div style={{ position: 'absolute', top: 0, left: 0, width: 640, height: 840 }}>
          <PlasticEnclosureSvg
            width={640}
            height={840}
            showDimensions={true}
            widthMm={400}
            heightMm={600}
          />
        </div>

        {/* 2. Cihazlar, Terminaller ve Kapak Katmanı */}
        <svg
          width={isDoorOpen ? 1280 : 640}
          height={840}
          viewBox={isDoorOpen ? '0 0 1280 840' : '0 0 640 840'}
          style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
        >
          {/* 0. Şebeke 3~ Faz Besleme Bloğu (L1, L2, L3) */}
          <g id="power-supply-block" style={{ pointerEvents: 'auto' }}>
            <rect x="94" y="70" width="72" height="42" rx="3" fill="#1e293b" stroke="#0f172a" strokeWidth="1.2" />
            <rect x="96" y="72" width="68" height="6" rx="1" fill="#334155" />
            <text x="130" y="77" fill="#cbd5e1" fontSize="5" fontWeight="bold" textAnchor="middle">
              3~ 400V ŞEBEKE
            </text>

            {[
              { id: 'L1', label: 'L1', x: 108, y: 92, color: '#78350f', pot: snapshot.nodePotentials['pwr:L1'] },
              { id: 'L2', label: 'L2', x: 130, y: 92, color: '#1f2937', pot: snapshot.nodePotentials['pwr:L2'] },
              { id: 'L3', label: 'L3', x: 152, y: 92, color: '#64748b', pot: snapshot.nodePotentials['pwr:L3'] },
            ].map((term) => {
              const termKey = `pwr:${term.id}`;
              const isSelected = activeTerminalId === termKey;
              return (
                <g
                  key={term.id}
                  transform={`translate(${term.x}, ${term.y})`}
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTerminalClick(termKey);
                  }}
                >
                  <rect x="-9" y="-10" width="18" height="24" rx="2" fill="#2b3442" stroke="#1a202c" strokeWidth="0.8" />
                  <circle cx="0" cy="-4" r="3" fill="#94a3b8" stroke="#475569" strokeWidth="0.6" />
                  <line x1="-2" y1="-4" x2="2" y2="-4" stroke="#334155" strokeWidth="0.8" />
                  <circle cx="0" cy="5" r="4.5" fill="#0f172a" stroke={isSelected ? '#38bdf8' : '#64748b'} strokeWidth={isSelected ? 2 : 1} />
                  <circle cx="0" cy="5" r="2.5" fill="#f59e0b" />
                  <text x="0" y="19" fill="#e2e8f0" fontSize="6.5" fontWeight="bold" textAnchor="middle">
                    {term.label}
                  </text>
                  {isSelected && (
                    <circle cx="0" cy="5" r="7" fill="none" stroke="#38bdf8" strokeWidth="1.5" strokeDasharray="2,2" />
                  )}
                </g>
              );
            })}
          </g>

          {/* Bakır Nötr Vidalı Klemens Dizisi İnteraktif Terminalleri (N1..N6) */}
          <g id="neutral-busbar-terminals" style={{ pointerEvents: 'auto' }}>
            {[229, 251, 273, 295, 317, 339].map((screwX, sIdx) => {
              const isSelected = activeTerminalId === 'pwr:N';
              return (
                <g
                  key={`n-interactive-${sIdx}`}
                  transform={`translate(${screwX}, 89)`}
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTerminalClick('pwr:N');
                  }}
                >
                  <title>Nötr Barası Klemensi N{sIdx + 1} — 0V N (Nötr)</title>
                  <circle cx="0" cy="0" r="8" fill="transparent" />
                  {isSelected && (
                    <circle cx="0" cy="0" r="9" fill="none" stroke="#38bdf8" strokeWidth="1.5" strokeDasharray="2,2" />
                  )}
                </g>
              );
            })}
          </g>

          {/* Bakır Toprak (PE) Vidalı Klemens Dizisi İnteraktif Terminalleri (PE1..PE6) */}
          <g id="earth-busbar-terminals" style={{ pointerEvents: 'auto' }}>
            {[426, 448, 470, 492, 514, 536].map((screwX, sIdx) => {
              const isSelected = activeTerminalId === 'pwr:PE';
              return (
                <g
                  key={`pe-interactive-${sIdx}`}
                  transform={`translate(${screwX}, 89)`}
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTerminalClick('pwr:PE');
                  }}
                >
                  <title>Toprak Barası Klemensi PE{sIdx + 1} — 0V PE (Toprak)</title>
                  <circle cx="0" cy="0" r="8" fill="transparent" />
                  {isSelected && (
                    <circle cx="0" cy="0" r="9" fill="none" stroke="#84cc16" strokeWidth="1.5" strokeDasharray="2,2" />
                  )}
                </g>
              );
            })}
          </g>

          {/* ============================================================ */}
          {/* KABLO KANALLARI (Wire Ducts: DIN EN 50085 Taraklı PVC Kanallar) */}
          {/* ============================================================ */}
          {/* 1. Üst Yatay Kablo Kanalı (duct_top: X=68, Y=145, W=504, H=34) */}
          {ductTop ? (
            <g style={{ pointerEvents: 'auto' }}>
              <WireDuctSvg
                x={ductTop.panelPlacement.x}
                y={ductTop.panelPlacement.y}
                width={(ductTop.props?.width as number) || 504}
                height={(ductTop.props?.height as number) || 34}
                orientation="horizontal"
                designator={ductTop.designator}
                name={ductTop.name}
                isSelected={selectedDeviceId === ductTop.id}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedDeviceId(selectedDeviceId === ductTop.id ? null : ductTop.id);
                }}
                onRemove={() => handleRemoveDevice(ductTop.id)}
              />
            </g>
          ) : (
            <PanelSlotGuide
              x={68}
              y={145}
              width={504}
              height={34}
              label="+ Üst Yatay Kablo Kanalı (504×34 mm)"
              sublabel="DIN EN 50085 Taraklı Kablo Kanalı"
              isHighlighted={selectedPaletteItemId === 'duct_top'}
              onClick={() => handlePlaceDevice('duct_top')}
            />
          )}

          {/* 2. Sol Dikey Kablo Kanalı (duct_left: X=48, Y=145, W=34, H=324) */}
          {ductLeft ? (
            <g style={{ pointerEvents: 'auto' }}>
              <WireDuctSvg
                x={ductLeft.panelPlacement.x}
                y={ductLeft.panelPlacement.y}
                width={(ductLeft.props?.width as number) || 34}
                height={(ductLeft.props?.height as number) || 324}
                orientation="vertical"
                designator={ductLeft.designator}
                name={ductLeft.name}
                isSelected={selectedDeviceId === ductLeft.id}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedDeviceId(selectedDeviceId === ductLeft.id ? null : ductLeft.id);
                }}
                onRemove={() => handleRemoveDevice(ductLeft.id)}
              />
            </g>
          ) : (
            <PanelSlotGuide
              x={48}
              y={145}
              width={34}
              height={324}
              label="+ Sol Dikey Kanal"
              orientation="vertical"
              isHighlighted={selectedPaletteItemId === 'duct_left'}
              onClick={() => handlePlaceDevice('duct_left')}
            />
          )}

          {/* 3. Sağ Dikey Kablo Kanalı (duct_right: X=558, Y=145, W=34, H=324) */}
          {ductRight ? (
            <g style={{ pointerEvents: 'auto' }}>
              <WireDuctSvg
                x={ductRight.panelPlacement.x}
                y={ductRight.panelPlacement.y}
                width={(ductRight.props?.width as number) || 34}
                height={(ductRight.props?.height as number) || 324}
                orientation="vertical"
                designator={ductRight.designator}
                name={ductRight.name}
                isSelected={selectedDeviceId === ductRight.id}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedDeviceId(selectedDeviceId === ductRight.id ? null : ductRight.id);
                }}
                onRemove={() => handleRemoveDevice(ductRight.id)}
              />
            </g>
          ) : (
            <PanelSlotGuide
              x={558}
              y={145}
              width={34}
              height={324}
              label="+ Sağ Dikey Kanal"
              orientation="vertical"
              isHighlighted={selectedPaletteItemId === 'duct_right'}
              onClick={() => handlePlaceDevice('duct_right')}
            />
          )}

          {/* 4. Orta Yatay Kablo Kanalı (duct_mid: X=68, Y=435, W=504, H=34) */}
          {ductMid ? (
            <g style={{ pointerEvents: 'auto' }}>
              <WireDuctSvg
                x={ductMid.panelPlacement.x}
                y={ductMid.panelPlacement.y}
                width={(ductMid.props?.width as number) || 504}
                height={(ductMid.props?.height as number) || 34}
                orientation="horizontal"
                designator={ductMid.designator}
                name={ductMid.name}
                isSelected={selectedDeviceId === ductMid.id}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedDeviceId(selectedDeviceId === ductMid.id ? null : ductMid.id);
                }}
                onRemove={() => handleRemoveDevice(ductMid.id)}
              />
            </g>
          ) : (
            <PanelSlotGuide
              x={68}
              y={435}
              width={504}
              height={34}
              label="+ Orta Yatay Kablo Kanalı (504×34 mm)"
              sublabel="DIN EN 50085 Taraklı Kablo Kanalı"
              isHighlighted={selectedPaletteItemId === 'duct_mid'}
              onClick={() => handlePlaceDevice('duct_mid')}
            />
          )}

          {/* ============================================================ */}
          {/* DIN RAYI ELEMANLARI (Sigortalar, Kontaktör, Termik Röle)      */}
          {/* ============================================================ */}
          {/* F1: 3 Kutuplu Güç Sigortası (X=100, Y=195) */}
          {hasF1 ? (
            <g
              transform="translate(100, 195)"
              style={{ pointerEvents: 'auto', cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedDeviceId(selectedDeviceId === 'f1' ? null : 'f1');
              }}
            >
              {selectedDeviceId === 'f1' && (
                <rect
                  x="-3"
                  y="-3"
                  width="94"
                  height="112"
                  rx="4"
                  fill="none"
                  stroke="#38bdf8"
                  strokeWidth="2"
                  strokeDasharray="4,2"
                />
              )}
              <McbSvg
                poles={3}
                designator="F1"
                ratedCurrent={16}
                isClosed={devicesState['f1']?.['closed'] !== false}
                isTripped={devicesState['f1']?.['tripped'] === true}
                onToggle={() => {
                  workshopAudio.playBreakerToggle();
                  const current = devicesState['f1']?.['closed'] !== false;
                  updateDeviceProp('f1', 'closed', !current);
                }}
                terminalValues={snapshot.nodePotentials}
                activeTerminalId={activeTerminalId?.startsWith('f1:') ? activeTerminalId.split(':')[1] : null}
                onTerminalClick={(tid) => handleTerminalClick(`f1:${tid}`)}
              />
            </g>
          ) : (
            <PanelSlotGuide
              x={100}
              y={195}
              width={88}
              height={106}
              label="+ F1 (3P 16A Sigorta)"
              sublabel="Ana Güç Sigortası"
              isHighlighted={selectedPaletteItemId === 'f1'}
              onClick={() => handlePlaceDevice('f1')}
            />
          )}

          {/* F2: 1 Kutuplu Kumanda Sigortası (X=196, Y=195) */}
          {hasF2 ? (
            <g
              transform="translate(196, 195)"
              style={{ pointerEvents: 'auto', cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedDeviceId(selectedDeviceId === 'f2' ? null : 'f2');
              }}
            >
              {selectedDeviceId === 'f2' && (
                <rect
                  x="-3"
                  y="-3"
                  width="46"
                  height="112"
                  rx="4"
                  fill="none"
                  stroke="#38bdf8"
                  strokeWidth="2"
                  strokeDasharray="4,2"
                />
              )}
              <McbSvg
                poles={1}
                designator="F2"
                ratedCurrent={6}
                isClosed={devicesState['f2']?.['closed'] !== false}
                isTripped={devicesState['f2']?.['tripped'] === true}
                onToggle={() => {
                  workshopAudio.playBreakerToggle();
                  const current = devicesState['f2']?.['closed'] !== false;
                  updateDeviceProp('f2', 'closed', !current);
                }}
                terminalValues={snapshot.nodePotentials}
                activeTerminalId={activeTerminalId?.startsWith('f2:') ? activeTerminalId.split(':')[1] : null}
                onTerminalClick={(tid) => handleTerminalClick(`f2:${tid}`)}
              />
            </g>
          ) : (
            <PanelSlotGuide
              x={196}
              y={195}
              width={40}
              height={106}
              label="+ F2"
              sublabel="1P 6A"
              isHighlighted={selectedPaletteItemId === 'f2'}
              onClick={() => handlePlaceDevice('f2')}
            />
          )}

          {/* KM1: 3 Kutuplu Güç Kontaktörü (X=244, Y=190) */}
          {hasKm1 ? (
            <g
              transform="translate(244, 190)"
              style={{ pointerEvents: 'auto', cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedDeviceId(selectedDeviceId === 'km1' ? null : 'km1');
              }}
            >
              {selectedDeviceId === 'km1' && (
                <rect
                  x="-3"
                  y="-3"
                  width="106"
                  height="121"
                  rx="4"
                  fill="none"
                  stroke="#38bdf8"
                  strokeWidth="2"
                  strokeDasharray="4,2"
                />
              )}
              <ContactorSvg
                designator="KM1"
                isEnergized={km1State?.energized ?? false}
                terminalValues={snapshot.nodePotentials}
                activeTerminalId={activeTerminalId?.startsWith('km1:') ? activeTerminalId.split(':')[1] : null}
                onTerminalClick={(tid) => handleTerminalClick(`km1:${tid}`)}
              />
            </g>
          ) : (
            <PanelSlotGuide
              x={244}
              y={190}
              width={100}
              height={115}
              label="+ KM1 (Güç Kontaktörü)"
              sublabel="3P + 1NO Bobin: 230V"
              isHighlighted={selectedPaletteItemId === 'km1'}
              onClick={() => handlePlaceDevice('km1')}
            />
          )}

          {/* FR1: Termik Aşırı Akım Rölesi (X=244, Y=305) */}
          {hasFr1 ? (
            <g
              transform="translate(244, 305)"
              style={{ pointerEvents: 'auto', cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedDeviceId(selectedDeviceId === 'fr1' ? null : 'fr1');
              }}
            >
              {selectedDeviceId === 'fr1' && (
                <rect
                  x="-3"
                  y="-3"
                  width="106"
                  height="101"
                  rx="4"
                  fill="none"
                  stroke="#38bdf8"
                  strokeWidth="2"
                  strokeDasharray="4,2"
                />
              )}
              <ThermalRelaySvg
                designator="FR1"
                isTripped={fr1State?.tripped ?? false}
                currentSettingAmps={5.0}
                onTripToggle={() => {
                  workshopAudio.playThermalTrip();
                  const current = devicesState['fr1']?.['tripped'] === true;
                  updateDeviceProp('fr1', 'tripped', !current);
                }}
                terminalValues={snapshot.nodePotentials}
                activeTerminalId={activeTerminalId?.startsWith('fr1:') ? activeTerminalId.split(':')[1] : null}
                onTerminalClick={(tid) => handleTerminalClick(`fr1:${tid}`)}
              />
            </g>
          ) : (
            <PanelSlotGuide
              x={244}
              y={305}
              width={100}
              height={95}
              label="+ FR1 (Termik Röle)"
              sublabel="KM1 Alt Montaj (5.0A)"
              isHighlighted={selectedPaletteItemId === 'fr1'}
              onClick={() => handlePlaceDevice('fr1')}
            />
          )}

          {/* ============================================================ */}
          {/* M1: 3 Fazlı Motor Gövdesi (Alt Düzlem: X=160, Y=530)        */}
          {/* ============================================================ */}
          {hasM1 ? (
            <g
              transform="translate(160, 530)"
              style={{ pointerEvents: 'auto', cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedDeviceId(selectedDeviceId === 'm1' ? null : 'm1');
              }}
            >
              {selectedDeviceId === 'm1' && (
                <rect
                  x="-4"
                  y="-4"
                  width="268"
                  height="198"
                  rx="6"
                  fill="none"
                  stroke="#38bdf8"
                  strokeWidth="2"
                  strokeDasharray="4,2"
                />
              )}
              <AcMotor3PhaseSvg
                width={260}
                height={190}
                isRunning={m1State?.motorRunning ?? false}
                isOverload={fr1State?.tripped ?? false}
                direction="cw"
                label="M1 — 3 Fazlı Asenkron Motor"
              />
              {/* Motor Klemens Uçları (U1, V1, W1, PE) */}
              <g transform="translate(0, 0)">
                {['U1', 'V1', 'W1', 'PE'].map((mTerm, idx) => {
                  const cx = 24 + idx * 20;
                  const cy = 20;
                  const mKey = `m1:${mTerm}`;
                  const pot = snapshot.nodePotentials[mKey];
                  const termColor =
                    pot === 'L1'
                      ? '#f59e0b'
                      : pot === 'L2'
                        ? '#ea580c'
                        : pot === 'L3'
                          ? '#cbd5e1'
                          : '#84cc16';
                  const hasPot = pot && pot !== 'FLOATING';

                  return (
                    <g
                      key={mTerm}
                      transform={`translate(${cx}, ${cy})`}
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTerminalClick(mKey);
                      }}
                    >
                      <circle cx="0" cy="0" r="5" fill="#18202c" stroke="#475569" strokeWidth="1" />
                      <circle cx="0" cy="0" r="3" fill="#cbd5e1" />
                      {hasPot && <circle cx="0" cy="0" r="6" fill="none" stroke={termColor} strokeWidth="1.5" />}
                      <text x="0" y="11" fill="#cbd5e1" fontSize="6" fontWeight="bold" textAnchor="middle">
                        {mTerm}
                      </text>
                    </g>
                  );
                })}
              </g>
            </g>
          ) : (
            <PanelSlotGuide
              x={160}
              y={530}
              width={260}
              height={190}
              label="+ M1 (3 Fazlı Asenkron Motor Kaidesi)"
              sublabel="1.5 kW / 1420 d/d Kaide Montaj Yuvası"
              isHighlighted={selectedPaletteItemId === 'm1'}
              onClick={() => handlePlaceDevice('m1')}
            />
          )}

          {/* Kablo Katmanı (Klemensler Arası İletken Hatlar - Kapak açıkken tam görünür) */}
          {isDoorOpen && (
            <WireRenderer
              wires={project.wires}
              terminalPositions={terminalPositions}
              wireEnergized={snapshot.wireEnergized}
              selectedWireId={selectedWireId}
              onSelectWire={(wid) => setSelectedWireId(wid)}
            />
          )}

          {/* Endüstriyel Menteşeli Pano Kapağı (Kapak Açıkken Sağda Kablolama Modu, Kapalıyken Ön Operatör Yüzü) */}
          <EnclosureDoorSvg
            isOpen={isDoorOpen}
            hasSb1={hasSb1}
            hasSb2={hasSb2}
            hasHl1={hasHl1}
            hasHl2={hasHl2}
            selectedPaletteItemId={selectedPaletteItemId}
            onPlaceDevice={handlePlaceDevice}
            stopPressed={devicesState['sb1']?.['pressed'] === true}
            startPressed={devicesState['sb2']?.['pressed'] === true}
            lampRunLit={hl1State?.energized ?? false}
            lampFaultLit={hl2State?.energized ?? false}
            onStopDown={() => {
              workshopAudio.playButtonClick();
              updateDeviceProp('sb1', 'pressed', true);
            }}
            onStopUp={() => updateDeviceProp('sb1', 'pressed', false)}
            onStartDown={() => {
              workshopAudio.playButtonClick();
              updateDeviceProp('sb2', 'pressed', true);
            }}
            onStartUp={() => updateDeviceProp('sb2', 'pressed', false)}
            terminalValues={snapshot.nodePotentials}
            activeTerminalId={activeTerminalId}
            onTerminalClick={(tid) => handleTerminalClick(tid)}
            onToggleDoor={handleToggleDoor}
          />
        </svg>
      </div>
    </div>
  );
};
